import { type ClientLogger, type ClientLogMetadata, logClientWarning } from '@/client/logging/client-logger';
import { ApiError, extractApiErrorDescriptor } from '@/shared/errors/api-error';
import {
    OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_CODE,
    OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_REASON,
    isOfferNotAcceptedMarketplaceErrorDescriptor,
} from '@/shared/errors/marketplace-api-error-message';

type OfferNotAcceptedPolicyDescriptor = {
    code?: unknown;
    errorCode?: unknown;
    reason?: unknown;
    errorReason?: unknown;
    message?: unknown;
    errorMessage?: unknown;
    category?: unknown;
    retryable?: unknown;
    operationId?: unknown;
    clientId?: unknown;
};

type OfferNotAcceptedPolicyMetadata = {
    category?: string;
    retryable?: boolean;
    operationId?: string;
    clientId?: string;
};

type OfferNotAcceptedPolicyWarningInput = {
    logger: ClientLogger;
    message: string;
    error: unknown;
    context: ClientLogMetadata & {
        action: string;
    };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]';

const readOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const normalized = value.trim();
    return normalized || undefined;
};

const readOptionalBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const toPolicyMetadata = (descriptor: OfferNotAcceptedPolicyDescriptor): OfferNotAcceptedPolicyMetadata => {
    const category = readOptionalString(descriptor.category);
    const retryable = readOptionalBoolean(descriptor.retryable);
    const operationId = readOptionalString(descriptor.operationId);
    const clientId = readOptionalString(descriptor.clientId);

    return {
        ...(category ? { category } : {}),
        ...(retryable !== undefined ? { retryable } : {}),
        ...(operationId ? { operationId } : {}),
        ...(clientId ? { clientId } : {}),
    };
};

const extractPolicyMetadataFromDescriptor = (
    descriptor: OfferNotAcceptedPolicyDescriptor | null,
): OfferNotAcceptedPolicyMetadata | null => {
    if (!descriptor || !isOfferNotAcceptedMarketplaceErrorDescriptor(descriptor)) {
        return null;
    }

    return toPolicyMetadata(descriptor);
};

const hasMachineReadablePolicyMarker = (descriptor: OfferNotAcceptedPolicyDescriptor): boolean =>
    readOptionalString(descriptor.code) === OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_CODE ||
    readOptionalString(descriptor.errorCode) === OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_CODE ||
    readOptionalString(descriptor.reason) === OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_REASON ||
    readOptionalString(descriptor.errorReason) === OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_REASON;

const readRecord = (value: unknown): Record<string, unknown> | null => (isRecord(value) ? value : null);

const extractPolicyMetadataFromError = (error: Error): OfferNotAcceptedPolicyMetadata | null => {
    const errorRecord = error as Error & Record<string, unknown>;
    const terminalEvent = readRecord(errorRecord.terminalEvent);
    const descriptor: OfferNotAcceptedPolicyDescriptor = {
        errorCode: errorRecord.errorCode ?? terminalEvent?.errorCode,
        errorReason: errorRecord.errorReason ?? terminalEvent?.errorReason,
        errorMessage: errorRecord.errorMessage ?? terminalEvent?.errorMessage,
        operationId: errorRecord.operationId ?? terminalEvent?.operationId,
        clientId: errorRecord.clientId ?? terminalEvent?.clientId,
    };

    if (!hasMachineReadablePolicyMarker(descriptor)) {
        return null;
    }

    return extractPolicyMetadataFromDescriptor(descriptor);
};

const extractOfferNotAcceptedPolicyMetadata = (error: unknown): OfferNotAcceptedPolicyMetadata | null => {
    if (error instanceof ApiError) {
        return extractPolicyMetadataFromDescriptor({
            code: error.code,
            message: error.message,
            category: error.category,
            retryable: error.retryable,
            operationId: error.operationId,
        });
    }

    if (error instanceof Error) {
        return extractPolicyMetadataFromError(error);
    }

    if (!isRecord(error)) {
        return null;
    }

    const responseData = isRecord(error.response) ? error.response.data : undefined;
    if (responseData !== undefined) {
        const responseDescriptor = extractPolicyMetadataFromDescriptor(extractApiErrorDescriptor(responseData));
        if (responseDescriptor) {
            return responseDescriptor;
        }
    }

    return (
        extractPolicyMetadataFromDescriptor(extractApiErrorDescriptor(error)) ??
        extractPolicyMetadataFromDescriptor(error)
    );
};

const mergePolicyMetadata = (
    context: ClientLogMetadata,
    policyMetadata: OfferNotAcceptedPolicyMetadata,
): ClientLogMetadata => {
    const operationId = readOptionalString(context.operationId) ?? policyMetadata.operationId;
    const clientId = readOptionalString(context.clientId) ?? policyMetadata.clientId;

    return {
        ...context,
        code: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_CODE,
        reason: OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_REASON,
        ...(policyMetadata.category ? { category: policyMetadata.category } : {}),
        ...(policyMetadata.retryable !== undefined ? { retryable: policyMetadata.retryable } : {}),
        ...(operationId ? { operationId } : {}),
        ...(clientId ? { clientId } : {}),
    };
};

export const logOfferNotAcceptedMarketplacePolicyWarning = ({
    logger,
    message,
    error,
    context,
}: OfferNotAcceptedPolicyWarningInput): boolean => {
    const policyMetadata = extractOfferNotAcceptedPolicyMetadata(error);
    if (!policyMetadata) {
        return false;
    }

    logClientWarning({
        logger,
        message,
        error,
        context: mergePolicyMetadata(context, policyMetadata),
    });

    return true;
};
